import React, { useEffect, useState } from "react";
import { Routes, Route, BrowserRouter, Navigate, useLocation } from "react-router-dom";
import "./App.css";
import "./assets/font/stylesheet.css";

import Alert from "./components/Alert";
import AuthRoutes from "./routing/AuthRoutes";
import PrivateRoutes from "./routing/PrivateRoutes";
import AnalyticsProvider from "./components/AnalyticsProvider";
import { ActivityFeedProvider } from "./context/ActivityFeedContext";
import RemindersNotification from "./components/RemindersNotification";
import PWAInstallPrompt from "./components/PWAInstallPrompt";
import DemoUserGuard from "./components/DemoUserGuard";
import CookieConsent from "./components/CookieConsent";
import MobileInputZoomFix from "./components/MobileInputZoomFix";
import { useDispatch, useSelector } from "react-redux";
import SupportHub from './pages/CustomerService/SupportHub';
import ServiceChart from './pages/CustomerService/ServiceChart';
import ProtectedFeedbackForm from './pages/FeedbackForm/ProtectedFeedbackForm';
import LoadingScreen from './components/LoadingScreen';
import { restorePageOpacity, preloadAssets } from './utils/smoothNavigation';

// Component to handle auto-refresh for specific problematic routes on mobile
function MobileScrollFix() {
  const location = useLocation();
  
  useEffect(() => {
    console.log('MobileScrollFix: Current path:', location.pathname);
    
    // Define problematic routes that need scroll fix
    const problematicRoutes = ['/', '/main'];
    const isVaultRoute = location.pathname.startsWith('/vault/');
    const isProblematicRoute = problematicRoutes.includes(location.pathname) || isVaultRoute;
    
    console.log('Is problematic route?', isProblematicRoute);
    
    if (!isProblematicRoute) {
      console.log('Not a problematic route, skipping');
      return;
    }
    
    // More aggressive mobile detection
    const isMobile = /iPhone|iPad|iPod|Android|webOS|BlackBerry|IEMobile|Opera Mini|Mobile|mobile|CriOS/i.test(navigator.userAgent) ||
                     (window.innerWidth <= 768) ||
                     ('ontouchstart' in window);
    
    console.log('Is mobile detected?', isMobile);
    console.log('User Agent:', navigator.userAgent);
    
    if (!isMobile) {
      console.log('Not mobile, skipping refresh');
      return;
    }

    // Create a unique key for this route to track if we've already refreshed
    const routeKey = location.pathname === '/' ? 'home' : 
                     location.pathname === '/main' ? 'main' : 
                     isVaultRoute ? 'vault' : 'unknown';
    const refreshKey = `mobile_scroll_fix_${routeKey}`;
    
    // Check if we've already refreshed this route in this session
    const hasAlreadyRefreshed = sessionStorage.getItem(refreshKey);
    
    console.log('Refresh key:', refreshKey);
    console.log('Has already refreshed?', hasAlreadyRefreshed);
    
    if (!hasAlreadyRefreshed) {
      console.log('🔄 TRIGGERING MOBILE SCROLL FIX REFRESH for', location.pathname);
      
      // Mark that we've refreshed this route BEFORE the refresh
      sessionStorage.setItem(refreshKey, 'true');
      
      // Immediate refresh
      window.location.reload();
    } else {
      console.log('✅ Route already refreshed, scroll should work for', location.pathname);
    }
  }, [location.pathname]);
  
  return null;
}

function App() {
  let tokenState = localStorage.getItem('dizeeToken');
  let bool = false
  if (tokenState) {
    bool = true
  }
  const [currentUser, setCurrentUser] = useState(bool);

  const auth = useSelector((state) => state.auth?.isLogin);
  const dispatch = useDispatch();
  
  useEffect(() => {
    const token = localStorage.getItem('dizeeToken');
    const isLoggedIn = token;
    setCurrentUser(isLoggedIn);
  }, [dispatch, auth, currentUser]);

  // Smooth page restoration after navigation
  useEffect(() => {
    restorePageOpacity();
    preloadAssets();
  }, []);

  // Enhanced keyboard handling with improved scrolling
  useEffect(() => {
    let isKeyboardOpen = false;
    const activeInputs = new Set();
    let originalScrollPosition = 0;
    let originalBodyStyles = {};
    
    // Modified touch handler to be less restrictive
    const preventTouchMove = (e) => {
      if (isKeyboardOpen) {
        // Check if we're touching an input or scrollable area
        let target = e.target;
        while (target && target !== document.body) {
          // Allow scrolling in inputs and scrollable containers
          if (activeInputs.has(target) || 
              ['auto', 'scroll'].includes(getComputedStyle(target).overflow) ||
              ['auto', 'scroll'].includes(getComputedStyle(target).overflowY)) {
            return; // Allow the scroll to happen naturally
          }
          target = target.parentElement;
        }
        
        // Only prevent default for non-scrollable areas when keyboard is open
        if (e.target.tagName !== 'INPUT' && 
            e.target.tagName !== 'TEXTAREA' && 
            e.target.tagName !== 'SELECT') {
          e.preventDefault();
        }
      }
    };
    
    // Save original page state before fixing position
    const saveOriginalState = () => {
      originalScrollPosition = window.pageYOffset;
      originalBodyStyles = {
        position: document.body.style.position,
        top: document.body.style.top,
        left: document.body.style.left,
        width: document.body.style.width,
        overflow: document.body.style.overflow
      };
    };
    
    // Fix position only for iOS devices with improved approach
    const fixPosition = () => {
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
      if (isIOS) {
        // Set a less restrictive style that still prevents background content shift
        document.body.style.position = 'fixed';
        document.body.style.top = `-${originalScrollPosition}px`;
        document.body.style.left = '0';
        document.body.style.width = '100%';
        // Don't restrict overflow completely
        document.body.style.overflow = 'hidden';
        document.body.dataset.scrollY = originalScrollPosition;
      }
    };
    
    // Restore original position and scroll state with better synchronization
    const restorePosition = () => {
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
      if (isIOS) {
        // Restore all original styles
        Object.keys(originalBodyStyles).forEach(key => {
          document.body.style[key] = originalBodyStyles[key] || '';
        });
        
        // Use setTimeout to ensure position is restored after style changes
        setTimeout(() => {
          window.scrollTo(0, originalScrollPosition);
        }, 10);
      }
    };
    
    // Track focused inputs
    const handleFocus = (e) => {
      if (e.target.tagName === 'INPUT' || 
          e.target.tagName === 'TEXTAREA' || 
          e.target.tagName === 'SELECT') {
        
        // First input being focused
        if (activeInputs.size === 0) {
          saveOriginalState();
          fixPosition();
        }
        
        activeInputs.add(e.target);
        isKeyboardOpen = true;
      }
    };
    
    const handleBlur = (e) => {
      if (e.target.tagName === 'INPUT' || 
          e.target.tagName === 'TEXTAREA' || 
          e.target.tagName === 'SELECT') {
        
        activeInputs.delete(e.target);
        
        // Only restore position if no inputs are active
        if (activeInputs.size === 0) {
          isKeyboardOpen = false;
          restorePosition();
        }
      }
    };
    
    // Handle device orientation changes and keyboard visibility
    const handleResize = () => {
      // Check if this is likely a keyboard closing
      const isLikelyKeyboardClose = isKeyboardOpen && 
                                    activeInputs.size === 0 && 
                                    document.activeElement.tagName !== 'INPUT' && 
                                    document.activeElement.tagName !== 'TEXTAREA' && 
                                    document.activeElement.tagName !== 'SELECT';
      
      if (isLikelyKeyboardClose) {
        isKeyboardOpen = false;
        restorePosition();
      }
    };
    
    // Set up all event listeners
    document.addEventListener('touchmove', preventTouchMove, { passive: false });
    document.addEventListener('focus', handleFocus, true);
    document.addEventListener('blur', handleBlur, true);
    window.addEventListener('resize', handleResize);
    
    return () => {
      document.removeEventListener('touchmove', preventTouchMove, { passive: false });
      document.removeEventListener('focus', handleFocus, true);
      document.removeEventListener('blur', handleBlur, true);
      window.removeEventListener('resize', handleResize);
      
      // Ensure we restore position on cleanup
      if (document.body.style.position === 'fixed') {
        restorePosition();
      }
    };
  }, []);
  
  return (
    <>
      <Alert />

      <BrowserRouter>
        <MobileScrollFix />
        <MobileInputZoomFix />
        <PWAInstallPrompt />
        <CookieConsent />
        <AnalyticsProvider>
          <Routes>
            {
              !currentUser ?
                <>
                  <Route path='/*' element={<AuthRoutes />} />
                  <Route path='*' element={<Navigate to='/' />} />
                </>
                :
                <>
                  <Route path='/*' element={
                    <ActivityFeedProvider>
                      <>
                        <RemindersNotification />
                        <PrivateRoutes />
                      </>
                    </ActivityFeedProvider>
                  } />
                  <Route path='*' element={<Navigate to='/' />} />
                </>
            }
            <Route path="/support" element={<SupportHub />} />
            <Route path="/support/chart" element={<ServiceChart />} />
            <Route path="/feedback" element={<ProtectedFeedbackForm />} />
          </Routes>
        </AnalyticsProvider>
      </BrowserRouter>
    </>
  );
}

export default App;